import {
    Component,
    Input,
    OnChanges,
    OnInit,
    Output,
    SimpleChange,
    ViewChild,
    ViewEncapsulation,
    EventEmitter,
} from '@angular/core';
import {
    SortDescriptor,
    orderBy,
    CompositeFilterDescriptor,
    filterBy,
    AggregateDescriptor,
    AggregateResult,
    aggregateBy,
} from '@progress/kendo-data-query';
import { TooltipDirective } from '@progress/kendo-angular-tooltip';
import { GridDataResult, PageChangeEvent, GridComponent } from '@progress/kendo-angular-grid';
import { Router } from '@angular/router';
import { ReusableFunctions } from '../../../shared/classes/reusable-functions';
import { IReportingCommon } from '../../../reports/interfaces/reporting-common-for-allcomponent';
import { ITableColumns } from '../../../reports/interfaces/reporting-data';
import { NoDataActionType, NoDataSourceType, NoDataTypes } from '../../interfaces/no-data';
import * as Highcharts from 'highcharts';
import { ExcelExportData } from '@progress/kendo-angular-excel-export';

export interface ITableDataProperties {
    action: NoDataActionType;
    gridData: Array<any>;
    searchTerm: string;
    componentTitle: string;
    initialPageSize: number;
    initialSkip: number;
    height?: number;
    maxHeight?: number;
    recordSelection: string[];
    kendoGridSelectById: string;
    hasData: boolean;
    isDataLoaded: boolean;
    class?: string;
    pageable: boolean;
    useLGScroll: boolean;
}

export interface ITableColumnProperties {
    columnProperties: ITableColumns[];
}

export interface IDataChange {
    field: ITableColumns;
    value: any;
}

export interface IPaginatedDataInfo {
    gridData: Array<any>;
    misc?: object;
}

@Component({
    selector: 'common-kendo-table',
    templateUrl: './common-kendo-table.component.html',
    styleUrls: ['./common-kendo-table.component.scss'],
    encapsulation: ViewEncapsulation.None,
})
export class CommonKendoTableComponent implements OnInit, OnChanges {
    constructor(private _router: Router, private _reusableFunctions: ReusableFunctions) {
        this.getExcelExportData = this.getExcelExportData.bind(this);
    }

    filter: CompositeFilterDescriptor;
    type: 'numeric' | 'input' = 'numeric';
    sortable = {
        mode: 'single',
        allowUnsort: true,
    };

    pageableShow = {
        info: true,
        type: this.type,
        pageSizes: true,
        reviousNext: 'previousNext',
        alwaysVisible: true,
    };

    pageable = {};
    aggregates: AggregateDescriptor[] = [];
    aggregateResult: AggregateResult;
    tableHeight: number;
    sort: SortDescriptor[];
    skip: number = 0;
    maxHeight: number;
    distinctCategories: object; //TODO: Check the use of this
    exportFileName: string;
    gridDataResult: GridDataResult;

    editing: boolean;
    adding: boolean;
    selectedIdentifier: string;
    editClicked: boolean;
    template: string; //TODO: tool tips
    kendoGridSelectById: string; //TODO: Check the use of this
    recordSelection: string[]; //TODO: Check the use of this
    noRecordsAvailable: boolean;
    noDataType: NoDataTypes;
    columnsProperties: ITableColumns[];
    showHeaderFilter = false;
    pageSize: number = 10;
    useLGScroll: boolean = false;

    @ViewChild(TooltipDirective) tooltipDir: TooltipDirective; //TODO: tool tips
    @Input() tableColumnProperties: ITableColumnProperties;
    @Input() tableDataProperties: ITableDataProperties;
    @Input() selectionChangeHandler: any;
    @Output() dataChangeEvent: EventEmitter<IDataChange> = new EventEmitter<IDataChange>();
    @Output() pageChangeEvent: EventEmitter<IPaginatedDataInfo> =
        new EventEmitter<IPaginatedDataInfo>();
    /*
    // TODOs ////////
        Fing different aproach to search tearm
        find diffrent way to ditect changes
        header filter nodata activation
        Tooltips
    */
    ngOnInit() {
        this.template = '';
        this.recordSelection = [];
        this.skip = this.tableDataProperties.initialSkip ?? this.skip;
        this.pageSize = this.tableDataProperties.initialPageSize ?? this.pageSize;
        this.noDataType = {
            action: NoDataActionType.pageStart,
            searchTerm: '',
            componentTitle: '',
            inputSource: NoDataSourceType.table,
        };
        this.pageable = this.tableDataProperties.pageable ? this.pageableShow : false;
        this.assignInnitialValues(this.tableDataProperties);
    }

    assignInnitialValues(tableDataProperties: ITableDataProperties) {
        this.setDataFormatting();
        this.getInnitialSorting();
        this.kendoGridSelectById = tableDataProperties.kendoGridSelectById;
        this.recordSelection = tableDataProperties.recordSelection;

        if (tableDataProperties.maxHeight) {
            this.tableHeight = null;
            this.maxHeight = tableDataProperties.maxHeight;
        } else {
            this.tableHeight = tableDataProperties.height;
            this.maxHeight = null;
        }
    }

    ngOnChanges(changes: { [property: string]: SimpleChange }) {
        let columnChange: SimpleChange = changes['tableColumnProperties'];
        if (columnChange && columnChange.currentValue) {
            this.columnsProperties = this.tableColumnProperties.columnProperties.filter(
                (col) => col.hidden !== true
            );
            this.setDataFormatting();
            this.columnsProperties.sort((a, b) => a.fieldOrder - b.fieldOrder);
            this.drawCharts();
        }

        let dataCange: SimpleChange = changes['tableDataProperties'];
        if (dataCange && dataCange.currentValue) {
            this.tableDataProperties.class = this.tableDataProperties.class ?? 'common-kendo-table';
            this.useLGScroll = this.tableDataProperties.useLGScroll ?? false;
            this.pageSize =
                this.tableDataProperties.pageable === false
                    ? 1000
                    : this.tableDataProperties.initialPageSize ?? this.pageSize;
            this.noRecordsAvailable =
                !this.tableDataProperties.hasData && this.tableDataProperties.isDataLoaded;
            this.loadGridData();
        }
    }

    getInnitialSorting() {
        this.sort = [];
        this.columnsProperties.forEach((item) => {
            if (item.isSortable && item.defaultSorting) {
                if (item.fieldId === 'bookmark') {
                    this.sort.splice(0, 0, {
                        field: item.fieldId,
                        dir: item.defaultSorting,
                    });
                } else {
                    this.sort.push({
                        field: item.fieldId,
                        dir: item.defaultSorting,
                    });
                }
            }
        });
    }

    private loadGridData() {
        this.gridDataResult = {
            data: [],
            total: 0,
        };

        let data = this.tableDataProperties.gridData;
        if (data && data.length > 0) {
            this.showHeaderFilter = true;
            this.setFilterCategories();
            this.noDataType.searchTerm = this.tableDataProperties.searchTerm;
            this.noDataType.componentTitle = this.tableDataProperties.componentTitle;

            data = this.doSearch(data);
            data = this.doHeaderFilter(data);
            let orderedData = orderBy(data, this.sort);
            this.gridDataResult = {
                data: orderedData.slice(this.skip, this.skip + this.pageSize),
                total: orderedData.length,
            };
            this.pageChangeEvent.emit({ gridData: this.gridDataResult.data });
            this.drawCharts();
            this.setAggreagation();
        } else {
            this.showHeaderFilter = false;
        }
    }

    drawCharts() {
        setTimeout(() => {
            this.columnsProperties.forEach((col) => {
                if (
                    col.chartProperties &&
                    col.chartProperties.chartToFieldId &&
                    col.chartProperties.chartDataField &&
                    this.gridDataResult &&
                    this.gridDataResult.data &&
                    col.chartProperties.inComponentChartBuilding
                ) {
                    this.gridDataResult.data.forEach((rData) => {
                        col.chartProperties.chartOptionParams['seriesData'] =
                            rData[col.chartProperties.chartDataField];
                        let id = col.fieldId + rData[col.chartProperties.chartToFieldId];
                        new Highcharts.Chart(
                            id,
                            col.chartProperties.chartOptionFunction(
                                col.chartProperties.chartOptionParams
                            )
                        );
                    });
                }
            });
        }, 0);
    }

    doSearch(hierarchyData: Array<any>) {
        let searchTearm = this.tableDataProperties.searchTerm;
        if (searchTearm && searchTearm.length > 0) {
            this.tableDataProperties.isDataLoaded = false;

            hierarchyData = hierarchyData.filter((data) => {
                let hasValues = false;
                this.columnsProperties.forEach((col) => {
                    let hasFieldValue = false;
                    if (col.dataType !== 'chart' && col.dataType !== 'icon') {
                        hasFieldValue = data[col.fieldId]
                            ?.toString()
                            .toLowerCase()
                            .includes(searchTearm.toLowerCase());

                        if (hasFieldValue) {
                            hasValues = true;
                            this.skip = 0;
                            return true;
                        }
                    }
                });
                return hasValues;
            });
        }
        if (hierarchyData.length === 0) {
            this.noRecordsAvailable = true;
        } else {
            this.noRecordsAvailable = false;
        }

        this.tableDataProperties.isDataLoaded = true;
        return hierarchyData;
    }

    exportToExcel(kendoGrid: GridComponent) {
        let dateTime = this._reusableFunctions.getCurrentDateTimeStamp();
        this.exportFileName = 'lg-directory_' + dateTime + '_.xlsx';
        kendoGrid.saveAsExcel();
    }

    sortChange(sort: SortDescriptor[]): void {
        this.sort = sort;
        this.loadGridData();
    }

    pageChange(event: PageChangeEvent): void {
        this.pageSize = event.take;
        this.skip = event.skip;
        this.loadGridData();
    }

    setDataFormatting() {
        this.columnsProperties.forEach((column) => {
            if (
                column.decimalPlaces != null &&
                (column.dataType === 'currency' ||
                    column.dataType === 'number' ||
                    column.dataType === 'percent')
            ) {
                column.dataFormat =
                    '1.' + column.decimalPlaces.toString() + '-' + column.decimalPlaces.toString();
            }
            column.headerClass =
                column.fieldId === 'bookmark'
                    ? 'table-header-styles-bookmark'
                    : 'table-header-styles';

            if (column.iconProperties) {
                column.iconProperties.iconClass = column.iconProperties.iconClassStart;
            }
        });
    }

    hyperLinkHandler(fieldInfo: ITableColumns, dataItem: IReportingCommon) {
        let hyperlinkPproperties = fieldInfo.hyperlinkPproperties;
        if (hyperlinkPproperties) {
            hyperlinkPproperties.queryParamsInfList.forEach((param) => {
                hyperlinkPproperties.queryParams[param.paramId] = dataItem[param.paramDataField];
            });

            this._router.navigate(
                [hyperlinkPproperties.hyperlinkUrlBase, hyperlinkPproperties.hyperlinkUrlSegment],
                { queryParams: hyperlinkPproperties.queryParams }
            );
            // TODO: Modify the belo section
        } else if (fieldInfo.modalName === 'loanDetails') {
            //this.openLoanDetailsModal(dataItem, fieldInfo);
        } else if (fieldInfo.isHierarchyEntity) {
            this._router.navigate(['/directory/record/' + dataItem[fieldInfo.hyperLinkTo_FieldId]]);
        } else if (fieldInfo.isEmployee) {
            this._router.navigate([
                'settings/employees/' + dataItem[fieldInfo.hyperLinkTo_FieldId],
            ]);
        }
    }

    cellClickHandler(event) {
        if (event && event.dataItem) {
            //if (event.column.field === 'bookmark') {
            //    //this.columnProperties.find((item) => item.);
            //    if (event.dataItem['iconClass'] === this.columnProperties) {
            //    }
            //    //this.clickBookmark(event.dataItem);
            //} else if (event.column.field === 'edit') {
            //    this.editClickedHandler();
            //}
            //else {
            //    //this.openRecord(event);
            //}
        }
    }

    iconClick(dataItem: IReportingCommon, fieldInfo: ITableColumns) {
        if (fieldInfo.fieldId === 'bookmark') {
            if (fieldInfo.iconProperties) {
                fieldInfo.iconProperties.iconClass =
                    fieldInfo.iconProperties.iconClass === fieldInfo.iconProperties.iconClassStart
                        ? fieldInfo.iconProperties.iconClassChange
                        : fieldInfo.iconProperties.iconClassStart;
            }
            //this.clickBookmark(event.dataItem);
        } else if (fieldInfo.fieldId === 'edit') {
            this.editClickedHandler();
            this.openRecord(dataItem, fieldInfo);
        }
    }

    openRecord(dataItem: IReportingCommon, fieldInfo: ITableColumns) {
        this.selectedIdentifier = dataItem['hierarchyIdentifier'];
        if (this.editClicked) {
            if (this.selectedIdentifier && fieldInfo && fieldInfo.fieldId === 'edit') {
                this.editing = true;
            }
        }
    }

    editClickedHandler() {
        //this.editClicked = true;
    }

    getColumnStyles(fieldInfo: ITableColumns) {
        let style = {
            'background-color': fieldInfo?.columnBackgroundColor ?? '',
            'text-align': fieldInfo.dataAlignment,
        };
        return style;
    }

    getCommonCellStyles(dataItem: IReportingCommon, fieldInfo: ITableColumns) {
        //let styles = {
        //    color: '',
        //    'background-color': fieldInfo?.backgroundDefaultColor ?? '',
        //    cursor: fieldInfo.isHyperlink ? 'pointer' : 'default',
        //};

        //if (fieldInfo.isHyperlink) {
        //    styles['color'] = '#0078D7';
        //    styles['cursor'] = 'pointer';
        //}

        //return styles;
        let styles = {
            color: '',
            'background-color': '',
        };

        if (dataItem) {
            let value = dataItem[fieldInfo.fieldId];
            if (fieldInfo.textDefaultColor && typeof fieldInfo.textDefaultColor === 'string') {
                styles['color'] = fieldInfo.textDefaultColor;
            }

            if (fieldInfo.textColorThresholdFileldId) {
                let thresoldValue = dataItem[fieldInfo.textColorThresholdFileldId];
                if (typeof thresoldValue === 'number' && typeof value === 'number') {
                    if (thresoldValue > value) {
                        styles['color'] = fieldInfo.textPositiveColor;
                    } else if (thresoldValue < value) {
                        styles['color'] = fieldInfo.textNegativeColor;
                    } else {
                        styles['color'] = fieldInfo.textDefaultColor;
                    }
                }
            }

            if (
                fieldInfo.backgroundColorThresholdFileldId &&
                value !== null &&
                value !== undefined
            ) {
                styles['line-height'] = '46px';
                styles['min-height'] = '46px';

                if (
                    fieldInfo.backgroundDefaultColor &&
                    typeof fieldInfo.backgroundDefaultColor === 'string'
                ) {
                    styles['background-color'] = fieldInfo.backgroundDefaultColor;
                }

                if (fieldInfo.backgroundColorThresholdFileldId) {
                    let thresoldValueBackground =
                        dataItem[fieldInfo.backgroundColorThresholdFileldId];
                    if (typeof thresoldValueBackground === 'number' && typeof value === 'number') {
                        if (thresoldValueBackground > value) {
                            styles['background-color'] = fieldInfo.backgroundPositiveColor;
                        } else if (thresoldValueBackground < value) {
                            styles['background-color'] = fieldInfo.backgroundNegativeColor;
                        } else {
                            styles['background-color'] = fieldInfo.backgroundDefaultColor;
                        }
                    }
                }
            }
            if (fieldInfo.setBackgroundColorByBand && value !== null && value !== undefined) {
                styles['line-height'] = '46px';
                styles['min-height'] = '46px';

                if (fieldInfo.backgroundColorSetFieldId) {
                    let bandId = dataItem[fieldInfo.backgroundColorSetFieldId];
                    //let colorInfo = this.bandColorList.find((band) => band.colorbandId === bandId);
                    //styles['background-color'] = colorInfo ? colorInfo.color : '';
                }
            }

            if (fieldInfo.isHyperlink) {
                styles['color'] = '#0078D7';
                styles['cursor'] = 'pointer';
            }
        }
        return styles;
    }

    getCellStyle(dataItem: IReportingCommon, fieldInfo: ITableColumns) {
        let style = this.getCommonCellStyles(dataItem, fieldInfo);

        let leftPadding = '5px';
        let rightPadding = '5px';

        if (fieldInfo.fieldId === this.columnsProperties[0].fieldId) {
            leftPadding = '12px';
        }

        if (fieldInfo.dataAlignment === 'right') {
            rightPadding = '10px';
        }

        style['padding'] = '0 ' + rightPadding + ' 5px ' + leftPadding;
        return style;
    }

    getHeaderStyles(fieldInfo: ITableColumns) {
        let leftPadding = '5px';
        let rightPadding = '5px';

        if (fieldInfo.fieldId === this.columnsProperties[0].fieldId) {
            leftPadding = '14px';
        }

        if (fieldInfo.dataAlignment === 'right') {
            rightPadding = '10px';
        }

        let styles = {
            color: fieldInfo.textDefaultColor ?? '#223540',
            'text-align': fieldInfo.headerAlignment,
            padding: '5px ' + rightPadding + ' 5px ' + leftPadding,
            'line-height': '40px',
            'background-color': fieldInfo?.headerBackgroundColor ?? '',
        };

        return styles;
    }

    handleHeaderFilterChange(filter: CompositeFilterDescriptor): void {
        this.filter = filter;
        this.loadGridData();
    }

    doHeaderFilter(filteredData) {
        // TODO: Move this date conversion logic to a efficient place
        if (this.columnsProperties && this.columnsProperties.length > 0) {
            this.columnsProperties.forEach((colInfo) => {
                if (colInfo.dataType === 'date') {
                    filteredData.map((item) => {
                        item[colInfo.fieldId] = item[colInfo.fieldId]
                            ? new Date(item[colInfo.fieldId])
                            : item[colInfo.fieldId];
                    });
                }
            });
        }

        if (this.filter && this.filter.filters.length > 0) {
            filteredData = filterBy(filteredData, this.filter);
        }
        return filteredData;
    }

    setFilterCategories() {
        this.distinctCategories = {};
        this.columnsProperties.forEach((colInfo) => {
            if (colInfo.filter && colInfo.filter === 'category') {
                let gridData = [...this.tableDataProperties.gridData];
                this.distinctCategories[colInfo.fieldId] = gridData.filter(
                    (x, idx, xs) =>
                        xs.findIndex((y) => y[colInfo.fieldId] === x[colInfo.fieldId]) === idx
                );

                this.distinctCategories[colInfo.fieldId].sort((a, b) =>
                    this._reusableFunctions.sortAlphaNumeric(a[colInfo.fieldId], b[colInfo.fieldId])
                );
            }
        });
    }

    // TODO: Tooltip logic
    public showTooltip(e: MouseEvent): void {
        const element = e.target as HTMLElement;
        if (
            (element.nodeName === 'TD' || element.nodeName === 'TH') &&
            element.offsetWidth < element.scrollWidth
        ) {
            this.tooltipDir.toggle(element);
        } else {
            this.tooltipDir.hide();
        }
    }

    capitalizeFirstLetter(word: string): string {
        var temp: string = String(word);
        return temp.charAt(0).toUpperCase() + temp.slice(1);
    }

    async getExcelExportData(): Promise<ExcelExportData> {
        //let allData: DirectoryEntityListModel[] = [];
        //try {
        //    const dataResponse: DirectoryEntityListModel[][] = await this.getExcelExportDataAsync();
        //    for (const dataSet of dataResponse) {
        //        allData.push(...dataSet);
        //    }

        //    allData = this.doSearch(allData);
        //    allData = this.doHeaderFilter(allData);
        //    allData.forEach((obj) => {
        //        obj['entityType'] = this.getRoleName(obj.roleOrder);
        //    });
        //} catch (error) {
        //    console.error(error);
        //}
        const result: ExcelExportData = {
            data: [], //orderBy(allData, this.allDataSort),
            group: [],
        };

        return result;
    }

    onStateChange(event) {
        //this.dataChangeEvent.emit(event);
    }

    getInputValue(value: string, field: ITableColumns) {
        let val = value;
        if (field?.inputProperties?.type === 'number') {
            val = (+value).toFixed(field.decimalPlaces);
        }
        return val;
    }

    onInputChange(dataItem: any, field: ITableColumns) {
        if (field.dataType === 'input' && field.inputProperties?.type === 'number') {
            let value = dataItem[field.fieldId];
            dataItem[field.fieldId] = +value;
        }
        let change: IDataChange = { field: field, value: dataItem };
        this.setAggreagation();
        this.dataChangeEvent.emit(change);
    }

    onCheckboxChange(dataItem: any, field: ITableColumns) {
        let change: IDataChange = { field: field, value: dataItem };
        this.setAggreagation();
        this.dataChangeEvent.emit(change);
    }

    calculateAggregation() {
        //TODO: Refactor here and add more aggregation types
        //TODO: Set formatting for the aggregation values
        //TODO: Check the issue related to aggregation
        //this.aggregateResult = aggregateBy(this.gridDataResult.data, this.aggregates);
    }

    setAggreagation() {
        let toGetArregateColumnList = this.columnsProperties.filter(
            (item) => item.aggregationType != null
        );
        this.aggregates = [];
        if (toGetArregateColumnList.length > 0) {
            toGetArregateColumnList.forEach((col) => {
                this.aggregates.push({
                    field: col.aggregationId,
                    aggregate: col.aggregationType as any,
                });
            });
            this.calculateAggregation();
        }
    }

    setIndividualDataFormatting(precision) {
        return `1.${precision}-${precision}`;
    }
}
